import { type RecordsTransfer } from '../api';
import { callout, openDialog } from '../dialog';
import { esc, fmtDate, fmtHours, html } from '../ui';

/**
 * The records transfer package.
 *
 * ML.A.305(h): when an aircraft is sold or leaves management, the continuing
 * airworthiness record set goes with it. The useful part of this screen is not
 * the inventory — it is the list of what is missing, because a handover found
 * to be incomplete after the aircraft has gone is not recoverable.
 */
export function showRecordsTransfer(pkg: RecordsTransfer): void {
  const count = (label: string, n: number) => html`
    <div><span class="lbl">${esc(label)}</span><b>${n}</b></div>
  `;

  openDialog<null>({
    wide: true,
    title: `Records transfer — ${pkg.aircraft.registration}`,
    sub: `${pkg.aircraft.type} · s/n ${pkg.aircraft.serialNumber} · ${pkg.aircraft.owner}`,
    primary: 'Close',
    body: () => html`
      ${pkg.complete
        ? callout('ok', '✓', html`
            The record set is complete. Everything ML.A.305(h) expects to travel with the aircraft is on
            file.
          `)
        : callout('exceed', '✕', html`
            <b>This record set is not complete.</b> A handover found to be incomplete after the aircraft has
            gone is not recoverable, so these are worth closing first:
            <ul style="margin:6px 0 0 18px">
              ${pkg.gaps.map((g) => `<li>${esc(g)}</li>`).join('')}
            </ul>
          `)}

      <div class="calc">
        ${count('Programmes', pkg.contents.programmes)}
        ${count('Certificates', pkg.contents.arcs)}
        ${count('Releases', pkg.contents.releaseCertificates)}
        ${count('Task compliances', pkg.contents.taskCompliances)}
        ${count('AD compliances', pkg.contents.directiveCompliances)}
        ${count('Components fitted', pkg.contents.componentsFitted)}
        ${count('Weighings', pkg.contents.weighings)}
        ${count('Flights', pkg.contents.flights)}
      </div>

      <div class="fset">
        <div class="lg">Counters at handover</div>
        <div class="kv">
          <div>
            <span class="lbl">Airframe</span><b>${fmtHours(pkg.aircraft.tsnHours ?? 0)} h</b>
            <small>${pkg.aircraft.countersAsOf ? `as of ${esc(fmtDate(pkg.aircraft.countersAsOf))}` : 'date not recorded'}</small>
          </div>
          <div><span class="lbl">Landings</span><b>${pkg.aircraft.landingsTotal.toLocaleString('en-GB')}</b></div>
          <div><span class="lbl">Cycles</span><b>${pkg.aircraft.cyclesTotal.toLocaleString('en-GB')}</b></div>
          <div><span class="lbl">Regime</span><b>${esc(pkg.aircraft.regime.replace(/_/g, '-'))}</b></div>
        </div>
      </div>

      ${pkg.withoutFilesCount
        ? callout('caution', '!', html`
            ${pkg.withoutFilesCount} record(s) exist as entries with no scanned file behind them:
            ${esc(pkg.withoutFiles.slice(0, 8).join(', '))}${pkg.withoutFilesCount > 8 ? ', and more' : ''}.
            The entries transfer either way, but the new operator will ask for the documents.
          `)
        : ''}

      <div class="fset">
        <div class="lg">Components fitted at handover</div>
        <div class="rows" style="max-height:220px;overflow:auto">
          ${pkg.components.length
            ? pkg.components
                .map(
                  (c) => html`
                    <div class="row" style="border-left-color:var(--${c.hasRelease ? 'ok' : 'exceed'})">
                      <div class="bd">
                        <div class="t">${esc(c.description)}</div>
                        <div class="m">
                          ${esc(c.position)} · ${esc(c.partNumber)}${c.serialNumber ? ` s/n ${esc(c.serialNumber)}` : ''}
                          · ${fmtHours(c.tsnHours ?? 0)} h · ${esc(c.lifeKind.replace(/_/g, ' ').toLowerCase())}
                          ${c.hasRelease ? '' : ' · no release document on file'}
                        </div>
                      </div>
                    </div>
                  `,
                )
                .join('')
            : '<div class="row"><div class="bd"><div class="m">No components recorded as fitted.</div></div></div>'}
        </div>
      </div>

      ${callout('info', 'i', esc(pkg.note))}
    `,
    collect: () => null,
    submit: async () => undefined,
    done: () => 'Closed',
  });
}
